/// <reference path="Is.ts" />

/**
 * Provides utility functions for manipulating cookies.
 */
namespace RS.Cookie
{
    // https://github.com/ScottHamper/Cookies

    // Used to ensure cookie keys do not collide with
    // built-in `Object` properties
    const cacheKeyPrefix = "cookey."; // Hurr hurr, :)
    
    const maxExpireDate = new Date('Fri, 31 Dec 9999 23:59:59 UTC');

    const defaults =
    {
        path: '/',
        secure: false
    };

    let cachedDocumentCookie: string;
    let cache: Map<string> = {};

    /**
     * Cookie options.
     */
    export interface Options
    {
        path: string;
        domain?: string;
        expires?: Date|number|string;
        secure: boolean;
    }

    const defaultOptions: Options =
    {
        path: "/",
        secure: false
    };

    /**
     * Gets the value of the specified cookie, or null if it doesn't exist.
     * @param key
     */
    export function get(key: string): string|null
    {
        if (cachedDocumentCookie !== document.cookie)
        {
            renewCache();
        }
        
        const value = cache[`${cacheKeyPrefix}${key}`];
        return value == null ? null : decodeURIComponent(value);
    }

    /**
     * Sets the value of the specified cookie.
     * @param key 
     * @param value 
     * @param options 
     */
    export function set(key: string, value?: string|number, options?: Partial<Options>): void
    {
        if (options != null)
        {
            options = { ...defaultOptions, ...options };
        }
        else
        {
            options = { ...defaultOptions };
        }
        if (options.expires != null) { options.expires = getExpiresDate(value == null ? -1 : options.expires).toString(); }
        document.cookie = generateCookieString(key, value, options);
    }

    /**
     * Expires the specified cookie.
     * @param key 
     * @param options 
     */
    export function expire(key: string, options?: Partial<Options>): void
    {
        set(key, undefined, options);
    }

    function isValidDate(date: any): date is Date
    {
        return date != null && Object.prototype.toString.call(date) === '[object Date]' && !isNaN(date.getTime());
    }

    function getExpiresDate(expires: number|string|Date, now?: Date): Date
    {
        now = now || new Date();

        if (Is.number(expires))
        {
            expires = expires === Infinity ? maxExpireDate : new Date(now.getTime() + expires * 1000);
        }
        else if (Is.string(expires))
        {
            expires = new Date(expires);
        }
        else if (!isValidDate(expires))
        {
            throw new Error("'expires' parameter cannot be converted to a valid Date instance");
        }
        return expires;
    }

    function generateCookieString(key: string, value: string|number, options): string
    {
        key = key.replace(/[^#$&+\^`|]/g, encodeURIComponent);
        key = key.replace(/\(/g, '%28').replace(/\)/g, '%29');
        value = (value + '').replace(/[^!#$&-+\--:<-\[\]-~]/g, encodeURIComponent);
        options = options || {};

        let cookieString = key + '=' + value;
        cookieString += options.path ? ';path=' + options.path : '';
        cookieString += options.domain ? ';domain=' + options.domain : '';
        cookieString += options.expires ? ';expires=' + options.expires.toUTCString() : '';
        cookieString += options.secure ? ';secure' : '';

        return cookieString;
    }

    function getCacheFromString(documentCookie?: string): Map<string>
    {
        const cookieCache = {};
        const cookiesArray = documentCookie ? documentCookie.split('; ') : [];

        for (let i = 0; i < cookiesArray.length; i++)
        {
            const cookieKvp = getKeyValuePairFromCookieString(cookiesArray[i]);

            if (cookieCache[`${cacheKeyPrefix}${cookieKvp.key}`] == null)
            {
                cookieCache[`${cacheKeyPrefix}${cookieKvp.key}`] = cookieKvp.value;
            }
        }

        return cookieCache;
    }

    function getKeyValuePairFromCookieString(cookieString: string): { key: string; value: string; }
    {
        // "=" is a valid character in a cookie value according to RFC6265, so cannot `split('=')`
        let separatorIndex = cookieString.indexOf('=');

        // IE omits the "=" when the cookie value is an empty string
        separatorIndex = separatorIndex < 0 ? cookieString.length : separatorIndex;

        const key = cookieString.substr(0, separatorIndex);
        let decodedKey;
        try
        {
            decodedKey = decodeURIComponent(key);
        }
        catch (e)
        {
            Log.error(`Could not decode cookie with key '${key}'`);
            Log.error(e);
        }
        
        return {
            key: decodedKey,
            value: cookieString.substr(separatorIndex + 1) // Defer decoding value until accessed
        };
    }

    function renewCache(): void
    {
        cache = getCacheFromString(document.cookie);
        cachedDocumentCookie = document.cookie;
    }

    function areEnabled(): boolean
    {
        const testKey = "RSCore";
        set(testKey, 1);
        const isEnabled = get(testKey) === '1';
        expire(testKey);
        return isEnabled;
    }

    /** Whether cookies are enabled in the current browser session or not. */
    export const enabled = areEnabled();
}